import { useTranslation } from 'react-i18next';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui-kit/table';
import { useIsMobile } from '@/hooks/use-media-query';
import { ResourceBreakdown } from '../../types/timesheet-manager.types';

const formatHours = (hours: number) => `${hours.toFixed(2)}h`;

export interface TimesheetBreakdownTableProps {
  resource: ResourceBreakdown;
}

/**
 * Renders the customer -> project rollup for a single resource, mirroring
 * the Angular source's nested resource/customer/project table
 * (timesheet-manager.component.html) flattened to one row per project since
 * this view is already scoped to a single resource.
 */
export const TimesheetBreakdownTable = ({ resource }: TimesheetBreakdownTableProps) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const rows = resource.Customers.flatMap((customer) =>
    customer.Projects.map((project) => ({ customer, project }))
  );

  if (rows.length === 0) {
    return <p className="text-sm text-neutral-500">{t('TIMESHEET_MANAGER_NO_ENTRIES_FOUND')}</p>;
  }

  // On a phone, per customer each project with its hours in a 4-cell grid, then the grand total
  // (the old portal's mobile timesheet card).
  if (isMobile) {
    const hourCells = (project: (typeof rows)[number]['project']) =>
      [
        [t('TIMESHEET_MANAGER_BILLABLE'), project.Billable],
        [t('TIMESHEET_MANAGER_GENERAL_ADMIN'), project.GeneralAdmin],
        [t('TIMESHEET_MANAGER_SALES_MARKETING'), project.SalesAndMarketing],
        [t('TIMESHEET_MANAGER_TOTAL'), project.Total],
      ] as const;
    return (
      <div className="flex flex-col gap-4 text-sm">
        {resource.Customers.filter((customer) => customer.Projects.length > 0).map((customer) => (
          <div key={customer.CustomerId} className="flex flex-col gap-3">
            <span className="break-words font-semibold">{customer.CustomerName}</span>
            {customer.Projects.map((project) => (
              <div key={project.ProjectId} className="flex flex-col gap-2 rounded-lg border p-3">
                <span className="break-words font-medium">{project.ProjectName}</span>
                <dl className="grid grid-cols-2 gap-x-4 gap-y-2">
                  {hourCells(project).map(([label, hours]) => (
                    <div key={label} className="flex min-w-0 flex-col gap-0.5">
                      <dt className="text-xs text-muted-foreground">{label}</dt>
                      <dd className="font-medium tabular-nums">{formatHours(hours)}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            ))}
          </div>
        ))}
        <div className="flex items-center justify-between border-t border-dashed pt-3 font-semibold">
          <span>{t('TIMESHEET_MANAGER_GRAND_TOTAL')}</span>
          <span className="tabular-nums">{formatHours(resource.GrandTotal)}</span>
        </div>
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t('TIMESHEET_MANAGER_CUSTOMER')}</TableHead>
          <TableHead>{t('TIMESHEET_MANAGER_PROJECT')}</TableHead>
          <TableHead>{t('TIMESHEET_MANAGER_BILLABLE')}</TableHead>
          <TableHead>{t('TIMESHEET_MANAGER_GENERAL_ADMIN')}</TableHead>
          <TableHead>{t('TIMESHEET_MANAGER_SALES_MARKETING')}</TableHead>
          <TableHead>{t('TIMESHEET_MANAGER_TOTAL')}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map(({ customer, project }) => (
          <TableRow key={`${customer.CustomerId}-${project.ProjectId}`}>
            <TableCell>{customer.CustomerName}</TableCell>
            <TableCell>{project.ProjectName}</TableCell>
            <TableCell>{formatHours(project.Billable)}</TableCell>
            <TableCell>{formatHours(project.GeneralAdmin)}</TableCell>
            <TableCell>{formatHours(project.SalesAndMarketing)}</TableCell>
            <TableCell className="font-medium">{formatHours(project.Total)}</TableCell>
          </TableRow>
        ))}
        <TableRow>
          <TableCell colSpan={5} className="text-right font-semibold">
            {t('TIMESHEET_MANAGER_GRAND_TOTAL')}
          </TableCell>
          <TableCell className="font-semibold">{formatHours(resource.GrandTotal)}</TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );
};
